import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ProviderService } from 'src/app/services/provider.service';
import { UtilsService } from 'src/app/services/utils.service';
import { QuotesService } from 'src/app/services/quotes.service';

@Component({
    selector: 'app-provider-add',
    templateUrl: './provider-add.component.html',
    styleUrls: ['./provider-add.component.css']
})
export class ProviderAddComponent implements OnInit {
    constructor(
        private fb: FormBuilder,
        private providerService: ProviderService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private router: Router,
        private route: ActivatedRoute,
        private quotesService: QuotesService,
        private el: ElementRef<HTMLElement>,
    ) { }

    providerId: number | null = null;
    isEdit = false;
    isSubmitted = false;

    providerTypes = ['Servicios', 'Arrendamiento', 'Insumos', 'Transporte', 'Otro'];
    accountTypes = ['Cuenta de Cheques', 'Cuenta de Débito', 'Tarjeta Empresarial'];
    recurrenceOptions = ['Único', 'Diario', 'Semanal', 'Quincenal', 'Mensual', 'Trimestral', 'Semestral', 'Anual'];
    readonly dayGridOptions: string[] = Array.from({ length: 31 }, (_, i) => String(i + 1));
    openDayPickerIndex: number | null = null;
    private readonly monthlyFamily = ['Mensual', 'Trimestral', 'Semestral', 'Anual'];
    /** Ids de las programaciones que ya existían al cargar el proveedor, para poder avisar si alguna se va a quitar. */
    private originalScheduleIds: number[] = [];
    banks: any[] = [];

    providerForm!: FormGroup;

    ngOnInit(): void {
        this.loadBanks();
        this.providerForm = this.fb.group({
            business_name: ['', Validators.required],
            trade_name: [''],
            provider_type: [''],
            rfc: ['', Validators.required],
            email: ['', Validators.email],
            phone: [''],
            fiscal_address: [''],
            bank_name: [''],
            beneficiary_name: [''],
            account_number: [''],
            clabe: [''],
            account_type: [''],
            payments: this.fb.array([]),
        });

        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            this.providerId = Number(id);
            this.isEdit = true;
            this.loadProvider(this.providerId);
        } else {
            this.addPaymentRow();
        }
    }

    private loadBanks(): void {
        this.quotesService.getBank().subscribe({
            next: (resp: any) => this.banks = resp || [],
            error: () => this.banks = [],
        });
    }

    get fc() {
        return this.providerForm.controls;
    }

    get payments(): FormArray {
        return this.providerForm.get('payments') as FormArray;
    }

    /**
     * "Único" y "Diario/Semanal/Quincenal" piden una fecha concreta
     * (fecha límite o fecha de inicio); la familia mensual sigue usando el
     * selector de día del mes ya existente ("Día de pago").
     */
    isMonthlyFamily(recurrence: string | null | undefined): boolean {
        return !!recurrence && this.monthlyFamily.includes(recurrence);
    }

    toggleDayPicker(index: number): void {
        this.openDayPickerIndex = this.openDayPickerIndex === index ? null : index;
    }

    selectDay(index: number, day: string): void {
        this.payments.at(index).get('due_day')?.setValue(day);
        this.openDayPickerIndex = null;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.openDayPickerIndex === null) return;
        const target = event.target as Node;
        const pickers = this.el.nativeElement.querySelectorAll('.day-picker');
        const clickedInsideAny = Array.from(pickers).some(p => p.contains(target));
        if (!clickedInsideAny) {
            this.openDayPickerIndex = null;
        }
    }

    private newPaymentRow(data?: any): FormGroup {
        return this.fb.group({
            id: [data?.id ?? null],
            concept: [data?.concept ?? '', Validators.required],
            recurrence: [data?.recurrence ?? '', Validators.required],
            due_day: [data?.due_day ?? ''],
            start_date: [data?.start_date ?? ''],
            amount: [data?.amount ?? '', [Validators.required, Validators.min(0)]],
            priority: [!!data?.priority],
        });
    }

    /** Reconstruye la opción de "Día de pago" a partir de una start_date guardada. */
    private dueDayFromDate(dateStr: string | null | undefined): string {
        if (!dateStr) return '';
        const d = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
        if (isNaN(d.getTime())) return '';
        const day = d.getDate();
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        if (day === lastDay) return 'Último día del mes';
        return String(day);
    }

    /** Trunca una fecha ISO devuelta por el backend a yyyy-MM-dd para <input type="date">. */
    private toDateInputValue(dateStr: string | null | undefined): string {
        return dateStr ? String(dateStr).slice(0, 10) : '';
    }

    addPaymentRow(): void {
        this.payments.push(this.newPaymentRow());
    }

    removePaymentRow(index: number): void {
        this.payments.removeAt(index);
    }

    /** Calcula la próxima fecha con ese día del mes; si ya pasó este mes, rueda al siguiente. */
    private computeDueDate(dueDay: string): string | null {
        if (!dueDay) return null;
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const buildDate = (year: number, month: number): Date => {
            const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
            if (dueDay === 'Último día del mes') {
                return new Date(year, month, lastDayOfMonth);
            }
            const day = parseInt(dueDay, 10);
            return new Date(year, month, Math.min(day, lastDayOfMonth));
        };

        let year = now.getFullYear();
        let month = now.getMonth();
        let candidate = buildDate(year, month);
        if (isNaN(candidate.getTime())) return null;

        if (candidate < today) {
            month += 1;
            if (month > 11) { month = 0; year += 1; }
            candidate = buildDate(year, month);
        }

        return candidate.toISOString().slice(0, 10);
    }

    /** Resuelve el due_date a enviar al backend según el tipo de recurrencia de la fila. */
    private resolveDueDate(row: any): string | null {
        if (this.isMonthlyFamily(row.recurrence)) {
            return this.computeDueDate(row.due_day);
        }
        return row.start_date || null;
    }

    loadProvider(id: number): void {
        this.spinner.show();
        this.providerService.getProvider(id).subscribe({
            next: (resp: any) => {
                const { payments, schedules, ...rest } = resp?.data || {};
                this.providerForm.patchValue(rest);

                this.payments.clear();
                this.originalScheduleIds = (schedules || []).map((s: any) => s.id);
                (schedules || []).forEach((s: any) => {
                    const monthly = this.isMonthlyFamily(s.recurrence);
                    this.payments.push(this.newPaymentRow({
                        id: s.id,
                        concept: s.concept,
                        recurrence: s.recurrence,
                        due_day: monthly ? this.dueDayFromDate(s.start_date) : '',
                        start_date: monthly ? '' : this.toDateInputValue(s.start_date),
                        amount: s.amount,
                        priority: s.priority,
                    }));
                });
                if (this.payments.length === 0) {
                    this.addPaymentRow();
                }

                this.spinner.hide();
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo cargar el proveedor', icon: 'error' });
            }
        });
    }

    async onSubmit(): Promise<void> {
        this.isSubmitted = true;

        if (this.providerForm.invalid) {
            this.utils.toastSwal({ title: 'Advertencia', text: 'Verifique que la información se encuentre completa.', icon: 'warning' });
            return;
        }

        const rawRows = this.providerForm.getRawValue().payments || [];
        const incompleteDate = rawRows.some((p: any) => p.concept && p.amount && p.recurrence && !this.resolveDueDate(p));
        if (incompleteDate) {
            this.utils.toastSwal({ title: 'Advertencia', text: 'Selecciona la fecha límite, la fecha de inicio o el día de pago de cada concepto.', icon: 'warning' });
            return;
        }

        const currentIds = rawRows.map((p: any) => p.id).filter((id: any) => !!id);
        const removedIds = this.originalScheduleIds.filter(id => !currentIds.includes(id));
        if (removedIds.length > 0) {
            const plural = removedIds.length > 1;
            const result = await this.utils.alertSwalConfir({
                title: 'Vas a eliminar conceptos de pago',
                text: `Se desactivará${plural ? 'n' : ''} ${removedIds.length} concepto${plural ? 's' : ''} de pago de este proveedor. `
                    + `No se generarán más pagos futuros para ${plural ? 'ellos' : 'él'}, pero el historial de pagos ya generado se conserva. ¿Confirmas?`,
                icon: 'warning',
                btn_confirm: 'Sí, eliminar',
            });
            if (!result.isConfirmed) {
                return;
            }
        }

        this.spinner.show();
        const formValue = this.providerForm.getRawValue();
        const payload: any = { ...formValue };

        payload.payments = (formValue.payments || [])
            .filter((p: any) => p.concept && p.amount && p.recurrence)
            .map((p: any) => ({
                id: p.id || null,
                concept: p.concept,
                recurrence: p.recurrence,
                due_date: this.resolveDueDate(p),
                amount: p.amount,
                priority: !!p.priority,
            }));

        const request$ = this.isEdit && this.providerId
            ? this.providerService.updateProvider(this.providerId, payload)
            : this.providerService.addProvider(payload);

        request$.subscribe({
            next: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación Exitosa', text: this.isEdit ? 'Proveedor actualizado correctamente' : 'Proveedor creado correctamente', icon: 'success' });
                this.router.navigateByUrl('/provider/list');
            },
            error: (error: any) => {
                this.spinner.hide();
                const message = error?.error?.errors ? Object.values(error.error.errors).flat().join(' ') : 'Se ha presentado un error al guardar el proveedor';
                this.utils.alertSwal({ title: 'Error', text: message, icon: 'error' });
            }
        });
    }
}
